import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { X, Loader2, Download } from "lucide-react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { PLYLoader } from "three/examples/jsm/loaders/PLYLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

// Universal file viewer modal: handles 2D images, STL/PLY, and DICOM (info only).
export default function FileViewer({ file, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [blob, setBlob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    let revoked = false;
    setLoading(true);
    setError(null);
    api
      .get(`/files/${file.id}/download`, { responseType: "blob" })
      .then((r) => {
        if (revoked) return;
        setBlob(r.data);
        setBlobUrl(URL.createObjectURL(r.data));
      })
      .catch((e) => setError(e.response?.data?.detail || "Download failed"))
      .finally(() => setLoading(false));
    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.id]);

  // 3D viewer
  useEffect(() => {
    if (!blob || file.category !== "scan_3d") return;
    const ext = file.original_filename.split(".").pop().toLowerCase();
    if (!["stl", "ply"].includes(ext)) return;
    const container = containerRef.current;
    if (!container) return;

    const width = container.clientWidth;
    const height = container.clientHeight || 480;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a101d);

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 5000);
    camera.position.set(0, 0, 200);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.appendChild(renderer.domElement);

    const amb = new THREE.AmbientLight(0xffffff, 0.55);
    scene.add(amb);
    const dir = new THREE.DirectionalLight(0xd4af37, 0.85);
    dir.position.set(120, 100, 200);
    scene.add(dir);
    const dir2 = new THREE.DirectionalLight(0xffffff, 0.4);
    dir2.position.set(-100, -80, -120);
    scene.add(dir2);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;

    const loader = ext === "stl" ? new STLLoader() : new PLYLoader();
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const geom = loader.parse(e.target.result);
        if (geom.computeVertexNormals) geom.computeVertexNormals();
        geom.center();
        const material = new THREE.MeshStandardMaterial({
          color: 0xf1efe3,
          metalness: 0.1,
          roughness: 0.55,
          flatShading: false,
        });
        const mesh = new THREE.Mesh(geom, material);
        scene.add(mesh);
        // Auto-fit camera
        const box = new THREE.Box3().setFromObject(mesh);
        const size = box.getSize(new THREE.Vector3()).length();
        camera.position.set(0, 0, size * 1.3);
        camera.near = size / 100;
        camera.far = size * 100;
        camera.updateProjectionMatrix();
        controls.target.set(0, 0, 0);
      } catch (err) {
        setError(`Could not render 3D file: ${err.message}`);
      }
    };
    reader.readAsArrayBuffer(blob);

    let frame = 0;
    const animate = () => {
      frame = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight || 480;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", onResize);
      controls.dispose();
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
    };
  }, [blob, file]);

  const isImage = file.category === "radiograph_2d";
  const isDicom = file.category === "dicom";
  const is3D = file.category === "scan_3d";

  const download = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = file.original_filename;
    a.click();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="file-viewer-overlay"
    >
      <div
        className="bg-[var(--pa-surface)] border border-[var(--pa-border-active)] rounded-lg max-w-5xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-[var(--pa-border)]">
          <div>
            <div className="kicker">{file.category.replace("_", " ")}</div>
            <div className="font-serif text-lg text-[var(--pa-text)] mt-0.5">
              {file.original_filename}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={download}
              data-testid="file-viewer-download"
              className="flex items-center gap-1.5 text-sm border border-[var(--pa-gold)] text-[var(--pa-gold)] px-3 py-1.5 rounded-md hover:bg-[var(--pa-gold)]/10 transition"
            >
              <Download size={14} /> Download
            </button>
            <button
              onClick={onClose}
              data-testid="file-viewer-close"
              className="text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)]"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6 min-h-[400px]" data-testid="file-viewer-body">
          {loading && (
            <div className="flex items-center justify-center h-full text-[var(--pa-text-muted)]">
              <Loader2 className="animate-spin mr-2" size={18} /> Loading file…
            </div>
          )}
          {error && <div className="text-red-400 text-sm">{error}</div>}

          {!loading && !error && isImage && blobUrl && (
            <img
              src={blobUrl}
              alt={file.original_filename}
              className="max-w-full mx-auto rounded border border-[var(--pa-border)]"
              data-testid="image-preview"
            />
          )}

          {!loading && !error && is3D && (
            <div
              ref={containerRef}
              data-testid="three-canvas"
              className="w-full h-[500px] rounded border border-[var(--pa-border)] bg-[var(--pa-bg-2)]"
            />
          )}

          {!loading && !error && isDicom && (
            <div className="text-center py-12">
              <Layers3Placeholder />
              <h3 className="font-serif text-xl text-[var(--pa-text)] mt-4">DICOM File</h3>
              <p className="mt-2 text-sm text-[var(--pa-text-muted)] max-w-md mx-auto">
                In-browser DICOM rendering for periapical/panoramic .dcm is available on download.
                Advanced CBCT volumetric slice navigation is coming soon. Use Download to open in
                your DICOM viewer (e.g. Galileos, Carestream, OnDemand3D).
              </p>
            </div>
          )}

          {!loading && !error && file.category === "exocad" && (
            <div className="text-center py-12">
              <FileCodePlaceholder />
              <h3 className="font-serif text-xl text-[var(--pa-text)] mt-4">
                exocad / Lab Construction File
              </h3>
              <p className="mt-2 text-sm text-[var(--pa-text-muted)] max-w-md mx-auto">
                Construction files are stored as-is. Download to open in exocad DentalCAD or
                forward to your lab.
              </p>
            </div>
          )}

          {!loading && !error && file.category === "other" && (
            <div className="text-center py-12 text-sm text-[var(--pa-text-muted)]">
              Preview not available for this file type. Use Download.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Layers3Placeholder() {
  return (
    <div className="mx-auto w-16 h-16 rounded-md border border-[var(--pa-gold)] flex items-center justify-center">
      <div className="font-serif text-[var(--pa-gold)] text-xl">.dcm</div>
    </div>
  );
}

function FileCodePlaceholder() {
  return (
    <div className="mx-auto w-16 h-16 rounded-md border border-[var(--pa-gold)] flex items-center justify-center">
      <div className="font-serif text-[var(--pa-gold)] text-xs">CAD</div>
    </div>
  );
}
